import * as React from 'react';
import { clsx, cn } from '@mentimeter/ragnar-tailwind-config';
import type { LinkT } from '../link';
import { Box, type BoxT } from '../box';
import { List } from '../list';
import {
  sizeVariants,
  type BreadcrumbVariantProps,
  type BreadcrumbSizeVariantProps,
} from './Breadcrumbs.variants';
import { BreadcrumbSeparator } from './BreadcrumbSeparator';
import { Breadcrumb } from './Breadcrumb';

export const isActivePath = (
  id: number | null | string,
  currentFolderId: number | null | undefined,
) => (currentFolderId ? String(currentFolderId) === String(id) : true);

export interface TreeT extends LinkT {
  label: string;
  id: string;
  wrapper?: ({
    children,
  }: {
    children: React.ReactElement;
  }) => React.ReactElement;
}

export interface BreadcrumbsT
  extends Pick<BreadcrumbVariantProps, 'capitalize'>,
    Pick<BreadcrumbSizeVariantProps, 'size'>,
    Pick<LinkT, 'underline'>,
    Omit<BoxT, 'onDrop'> {
  tree: Array<TreeT>;
  id: string;
  currentFolderId?: number | null;
}

export const DummyWrapper = ({ children }: { children: React.ReactElement }) =>
  children;

export const Breadcrumbs = ({
  id,
  tree,
  size,
  underline,
  currentFolderId,
  className,
  capitalize,
  ...props
}: BreadcrumbsT) => {
  if (!tree?.[0]) return null;

  return (
    <Box
      as="nav"
      aria-label="Breadcrumb"
      className={clsx(['w-full', 'flex-row', className])}
      {...props}
    >
      <List as="ol" className={clsx(['flex', 'flex-row', 'w-full'])}>
        {tree.map(
          (
            { id, href, label, onClick, wrapper: Wrapper = DummyWrapper },
            index,
          ) => (
            <li
              key={id}
              className={cn(
                'flex',
                'flex-row',
                'truncate',
                'max-w-[7em]',
                index === 0 && 'max-w-none',
                isActivePath(id, currentFolderId) && 'max-w-none',
              )}
            >
              {index > 0 && (
                <BreadcrumbSeparator className={sizeVariants({ size })} />
              )}
              <Wrapper>
                <Breadcrumb
                  size={size}
                  onClick={onClick}
                  href={href}
                  id={id}
                  underline={underline}
                  capitalize={capitalize}
                  isActivePath={isActivePath(id, currentFolderId)}
                  isRoot={index === 0}
                  {...(index === tree.length - 1 && {
                    'aria-current': 'page',
                  })}
                >
                  {label}
                </Breadcrumb>
              </Wrapper>
            </li>
          ),
        )}
      </List>
    </Box>
  );
};
